// src/components/templates/diary/ToolsView.jsx
// Comprehensive Tools View with Marketplace Integration
import React, { useState, useEffect, useMemo } from 'react';
import {
  Wrench, Plus, Search, Grid, List, MoreVertical, Edit2, Trash2, Eye, Layers, User,
  ExternalLink, ArrowLeft, Play, Code, Globe, Calculator, FileText, Heart, Scale, Truck,
  BarChart3, Calendar, ClipboardList, Receipt, Target, AlertTriangle,
  Package, Zap, Star, X, Check, Settings, Download, Store, ChevronRight,
  Trash, RefreshCw
} from 'lucide-react';
import { Button } from './UIComponents';
import { toolsService, TOOL_CATALOG, ToolCategory, ToolType, ToolPricing, InstallationStatus } from '../../services/toolsService';
import ToolsMarketplace from './ToolsMarketplace';

// Import built-in tool components
import SchoolStrategicTool from '../tools/education/SSEToSIP';
import InvoiceGeneratorTool from '../tools/business/InvoiceGeneratorTool';
import BusinessModelCanvasTool from '../tools/business/BusinessModelCanvasTool';
import TeamManagerTool from '../tools/management/TeamManagerTool';
import OperationalPlannerTool from '../tools/management/OperationalPlannerTool';

// Built-in tool component registry
const BUILT_IN_COMPONENTS = {
  'school-strategic': SchoolStrategicTool,
  'invoice-generator': InvoiceGeneratorTool,
  'business-model-canvas': BusinessModelCanvasTool,
  'team-manager': TeamManagerTool,
  'operational-planner': OperationalPlannerTool
  // Add more built-in tools here as they're developed
};

// Icon mapping
const IconMap = {
  Target, Receipt, Calendar, AlertTriangle, Zap, BarChart3, FileText,
  Package, Code, Globe, Calculator, ClipboardList
};

// View states
const ViewState = {
  DASHBOARD: 'DASHBOARD',
  MARKETPLACE: 'MARKETPLACE',
  TOOL_RUNNING: 'TOOL_RUNNING',
  CUSTOM_EDITOR: 'CUSTOM_EDITOR',
  CUSTOM_VIEWER: 'CUSTOM_VIEWER',
  SETTINGS: 'SETTINGS'
};

const CategoryConfig = {
  [ToolCategory.PRODUCTIVITY]: { icon: Zap, label: 'Productivity', color: 'blue', gradient: 'from-blue-500 to-cyan-500' },
  [ToolCategory.FINANCE]: { icon: Receipt, label: 'Finance', color: 'emerald', gradient: 'from-emerald-500 to-teal-500' },
  [ToolCategory.EDUCATION]: { icon: Target, label: 'Education', color: 'purple', gradient: 'from-purple-500 to-indigo-500' },
  [ToolCategory.PLANNING]: { icon: Calendar, label: 'Planning', color: 'amber', gradient: 'from-amber-500 to-orange-500' },
  [ToolCategory.ANALYSIS]: { icon: BarChart3, label: 'Analysis', color: 'cyan', gradient: 'from-cyan-500 to-blue-500' },
  [ToolCategory.DOCUMENTS]: { icon: FileText, label: 'Documents', color: 'orange', gradient: 'from-orange-500 to-red-500' },
  [ToolCategory.LOGISTICS]: { icon: Truck, label: 'Logistics', color: 'indigo', gradient: 'from-indigo-500 to-purple-500' },
  [ToolCategory.LEGAL]: { icon: Scale, label: 'Legal', color: 'rose', gradient: 'from-rose-500 to-pink-500' },
  [ToolCategory.HEALTHCARE]: { icon: Heart, label: 'Healthcare', color: 'pink', gradient: 'from-pink-500 to-rose-500' },
  [ToolCategory.CUSTOM]: { icon: Package, label: 'Custom', color: 'slate', gradient: 'from-slate-500 to-gray-500' }
};

// ============================================================
// HELPER COMPONENTS
// ============================================================

const StatCard = ({ icon: Icon, label, value, color = 'indigo', onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all text-left w-full ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-50`}>
      <Icon className={`w-6 h-6 text-${color}-600`} />
    </div>
    <div className="flex-1">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
    {onClick && <ChevronRight className="w-5 h-5 text-gray-300" />}
  </button>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 mb-6 max-w-sm">{description}</p>
    {action}
  </div>
);

const PricingBadge = ({ pricing }) => {
  if (pricing === ToolPricing.FREE) {
    return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">Free</span>;
  }
  if (pricing === ToolPricing.FREEMIUM) {
    return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">Freemium</span>;
  }
  if (pricing === ToolPricing.PAID) {
    return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">Premium</span>;
  }
  return null;
};

// ============================================================
// INSTALLED TOOL CARD
// ============================================================

const InstalledToolCard = ({ tool, onLaunch, onUninstall, onSettings, customStyling }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const category = CategoryConfig[tool.category] || CategoryConfig[ToolCategory.CUSTOM];
  const IconComponent = IconMap[tool.icon] || category.icon;
  const isBuiltIn = tool.type === ToolType.BUILT_IN;

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-gray-200 shadow-md transition-all group">
      {/* Header */}
      <div className={`h-36 bg-gradient-to-br ${category.gradient} flex items-center justify-center relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>
        
        <div className="relative">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
            <IconComponent className="w-8 h-8 text-gray-100" />
          </div>
        </div>
        
        {tool.featured && (
          <div className="absolute top-2 left-2">
            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
          </div>
        )}
        
        <div className="absolute top-2 right-2 flex items-center gap-2">
          {isBuiltIn && (
            <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
              Built-in
            </span>
          )}
          
          <div className="relative">
            <button 
              onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
              className="p-1.5 bg-white/80 hover:bg-white rounded-lg shadow-sm"
            >
              <MoreVertical className="w-4 h-4 text-gray-600" />
            </button>
            
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg border border-gray-100 z-50 py-1">
                  <button 
                    onClick={() => { onLaunch(tool); setMenuOpen(false); }} 
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Play className="w-4 h-4" /> Launch
                  </button>
                  <button 
                    onClick={() => { onSettings(tool); setMenuOpen(false); }} 
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Settings className="w-4 h-4" /> Settings
                  </button>
                  <hr className="my-1" />
                  <button 
                    onClick={() => { 
                      if (window.confirm(`Uninstall ${tool.name}? Your data will be preserved.`)) {
                        onUninstall(tool.id);
                      }
                      setMenuOpen(false); 
                    }} 
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash className="w-4 h-4" /> Uninstall
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-2">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
            {tool.customName || tool.name}
          </h3>
        </div>
        
        <p className="text-sm text-gray-500 line-clamp-2 mb-4">
          {tool.customDescription || tool.description}
        </p>
        
        {/* Meta info */}
        <div className="flex items-center gap-4 mb-4 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <Layers className="w-3.5 h-3.5" />
            v{tool.version}
          </span>
          <span className="flex items-center gap-1">
            <User className="w-3.5 h-3.5" />
            {tool.author}
          </span>
        </div>
        
        {/* Tags */}
        {tool.tags && tool.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {tool.tags.slice(0, 3).map(tag => (
              <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-md">
                {tag}
              </span>
            ))}
            {tool.tags.length > 3 && (
              <span className="px-2 py-0.5 text-gray-400 text-xs">
                +{tool.tags.length - 3}
              </span>
            )}
          </div>
        )}
        
        <button
          onClick={() => onLaunch(tool)}
          className="w-full py-2.5 px-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm rounded-lg flex items-center justify-center gap-2 transition-colors"
            style={{ backgroundColor: customStyling.primaryColor}}
        
        >
          <Play className="w-4 h-4" /> Launch Tool
        </button>
      </div>
    </div>
  );
};

// ============================================================
// CUSTOM TOOL CARD
// ============================================================

const CustomToolCard = ({ tool, onOpen, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-gray-200 hover:shadow-md transition-all">
      <div className="h-24 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center relative">
        {tool.type === ToolType.EXTERNAL ? (
          <Globe className="w-10 h-10 text-gray-400" />
        ) : (
          <Code className="w-10 h-10 text-gray-400" />
        )}
        
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs font-medium rounded-full">
            Custom
          </span>
        </div>
      </div>
      
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-1 truncate">{tool.name}</h3>
        <p className="text-sm text-gray-500 line-clamp-2 mb-3">{tool.description || 'No description'}</p>
        
        <div className="flex gap-2">
          <button
            onClick={() => onOpen(tool)}
            className="flex-1 py-2 px-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium text-sm rounded-lg flex items-center justify-center gap-2"
          >
            {tool.type === ToolType.EXTERNAL ? <ExternalLink className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            Open
          </button>
          <button
            onClick={() => onEdit(tool)}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              if (window.confirm('Delete this custom tool?')) onDelete(tool.id);
            }}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// CUSTOM TOOL EDITOR
// ============================================================

const CustomToolEditor = ({ tool, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '', description: '', type: ToolType.EXTERNAL, url: '', embedCode: '',
    ...tool
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    onSave(formData);
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h2 className="text-xl font-bold text-gray-900">
          {tool?.id ? 'Edit Custom Tool' : 'Create Custom Tool'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-xl border border-gray-100 p-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tool Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            placeholder="My Custom Tool"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            rows={3}
            placeholder="What does this tool do?"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
            className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
          >
            <option value={ToolType.EXTERNAL}>External Link</option>
            <option value={ToolType.INTERNAL}>Embedded (HTML/iframe)</option>
          </select>
        </div>

        {formData.type === ToolType.EXTERNAL && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
            <input
              type="url"
              value={formData.url}
              onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
              placeholder="https://..."
              required={formData.type === ToolType.EXTERNAL}
            />
          </div>
        )}

        {formData.type === ToolType.INTERNAL && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Embed Code</label>
            <textarea
              value={formData.embedCode}
              onChange={(e) => setFormData(prev => ({ ...prev, embedCode: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl font-mono text-sm"
              rows={8}
              placeholder="<iframe src='...'></iframe>"
            />
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button type="submit" className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2">
            <Check className="w-4 h-4" /> {tool?.id ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ============================================================
// MAIN TOOLS VIEW COMPONENT
// ============================================================

const DiaryToolsView = ({ 
  bookId, 
  customStyling = {},
  workspaces = [],
  isOwner = false,
  user = null
}) => {
  const [viewState, setViewState] = useState(ViewState.DASHBOARD);
  const [installedTools, setInstalledTools] = useState([]);
  const [customTools, setCustomTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTool, setActiveTool] = useState(null);
  const [editingCustomTool, setEditingCustomTool] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Load data on mount
  useEffect(() => {
    loadTools();
  }, [bookId]);

 const loadTools = async () => {
    setLoading(true);
    try {
      // Get workspace IDs user has access to (always include personal/default)
      const userWorkspaceIds = workspaces.length > 0 
        ? workspaces.map(w => w.id)
        : ['default', `personal-${user?.uid}`].filter(Boolean);
      
      const [installed, custom] = await Promise.all([
        toolsService.getInstalledTools(bookId, userWorkspaceIds),
        toolsService.getCustomTools(bookId, userWorkspaceIds)
      ]);
      setInstalledTools(installed);
      setCustomTools(custom);
    } catch (error) {
      console.error('Error loading tools:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get installed tool IDs for marketplace
  const installedToolIds = useMemo(() => {
    return installedTools.map(t => t.toolId || t.id);
  }, [installedTools]);

  // Filter tools by search
  const filteredInstalled = useMemo(() => {
    if (!searchQuery) return installedTools;
    const q = searchQuery.toLowerCase();
    return installedTools.filter(t => 
      t.name?.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q)
    );
  }, [installedTools, searchQuery]);

  const filteredCustom = useMemo(() => {
    if (!searchQuery) return customTools;
    const q = searchQuery.toLowerCase();
    return customTools.filter(t => 
      t.name?.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q)
    );
  }, [customTools, searchQuery]);

  // Handlers
  const handleInstallTool = async (toolId, workspaceId = null) => {
    try {
      await toolsService.installTool(bookId, toolId, workspaceId);
      await loadTools();
    } catch (error) {
      console.error('Error installing tool:', error);
      throw error;
    }
  };

  const handleUninstallTool = async (installationId) => {
    try {
      // installationId is the auto-generated doc ID, not toolId
      await toolsService.uninstallTool(bookId, installationId);
      await loadTools();
    } catch (error) {
      console.error('Error uninstalling tool:', error);
    }
  };

  const handleUpdateTool = async (toolId, updates) => {
    try {
      await toolsService.updateToolInstallation(bookId, toolId, updates);
      await loadTools();
    } catch (error) {
      console.error('Error updating tool:', error);
      throw error;
    }
  };

  const handleLaunchTool = (tool) => {
    if (tool.type === ToolType.BUILT_IN) {
      setActiveTool(tool);
      setViewState(ViewState.TOOL_RUNNING);
    } else if (tool.type === ToolType.EXTERNAL) {
      window.open(tool.url, '_blank');
    } else {
      setActiveTool(tool);
      setViewState(ViewState.CUSTOM_VIEWER);
    }
  };

  const handleOpenCustomTool = (tool) => {
    if (tool.type === ToolType.EXTERNAL) {
      window.open(tool.url, '_blank');
    } else {
      setActiveTool(tool);
      setViewState(ViewState.CUSTOM_VIEWER);
    }
  };

  const handleSaveCustomTool = async (tool) => {
    try {
      await toolsService.saveCustomTool(bookId, tool);
      await loadTools();
      setEditingCustomTool(null);
      setViewState(ViewState.DASHBOARD);
    } catch (error) {
      console.error('Error saving custom tool:', error);
    }
  };

  const handleDeleteCustomTool = async (toolId) => {
    try {
      await toolsService.deleteCustomTool(bookId, toolId);
      await loadTools();
    } catch (error) {
      console.error('Error deleting custom tool:', error);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Marketplace view
  if (viewState === ViewState.MARKETPLACE) {
    return (
      <ToolsMarketplace
        bookId={bookId}
        installedTools={installedTools}
        workspaces={workspaces}
        isOwner={isOwner}
        onInstall={handleInstallTool}
        onUpdateTool={handleUpdateTool}
        onBack={() => setViewState(ViewState.DASHBOARD)}
        customStyling={customStyling}
      />
    );
  }

  // Running built-in tool
  if (viewState === ViewState.TOOL_RUNNING && activeTool) {
    // Use toolId for component lookup (id is now auto-generated doc ID)
    const ToolComponent = BUILT_IN_COMPONENTS[activeTool.toolId || activeTool.id];
    
    if (!ToolComponent) {
      return (
        <div className="flex flex-col items-center justify-center h-64">
          <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
          <p className="text-gray-600">Tool component not found</p>
          <button
            onClick={() => setViewState(ViewState.DASHBOARD)}
            className="mt-4 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Back to Dashboard
          </button>
        </div>
      );
    }

    return (
      <div className="h-full flex-1 flex-col overflow-y-auto">
      <ToolComponent
        bookId={bookId}
        toolsService={toolsService}
        customStyling={customStyling}
        onBack={() => { setActiveTool(null); setViewState(ViewState.DASHBOARD); }}
        user={user}
        workspaceId={activeTool.workspaceId || 'default'}
        installationId={activeTool.id}
      />
      </div>
    );
  }

  // Custom tool viewer
  if (viewState === ViewState.CUSTOM_VIEWER && activeTool) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-white">
          <div className="flex items-center gap-3">
            <button onClick={() => { setActiveTool(null); setViewState(ViewState.DASHBOARD); }} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h2 className="font-semibold text-gray-900">{activeTool.name}</h2>
              <p className="text-sm text-gray-500">{activeTool.description}</p>
            </div>
          </div>
        </div>
        <div className="flex-1 bg-gray-50 p-4">
          {activeTool.embedCode ? (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden h-full" dangerouslySetInnerHTML={{ __html: activeTool.embedCode }} />
          ) : activeTool.url ? (
            <iframe src={activeTool.url} className="w-full h-full min-h-[600px] rounded-xl shadow-sm bg-white" title={activeTool.name} />
          ) : (
            <EmptyState icon={Code} title="No content" description="Configure embed code or URL for this tool." />
          )}
        </div>
      </div>
    );
  }

  // Custom tool editor
  if (viewState === ViewState.CUSTOM_EDITOR) {
    return (
      <CustomToolEditor
        tool={editingCustomTool}
        onSave={handleSaveCustomTool}
        onCancel={() => { setEditingCustomTool(null); setViewState(ViewState.DASHBOARD); }}
      />
    );
  }

  // Dashboard view
  return (
    <div className="h-full overflow-y-auto">
      <div className="p-3 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tools</h1>
            <p className="text-gray-500 mt-1">Productivity tools for your workspace</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setEditingCustomTool(null); setViewState(ViewState.CUSTOM_EDITOR); }}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Custom Tool
            </button>
            <button
              onClick={() => setViewState(ViewState.MARKETPLACE)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              style={{ backgroundColor: customStyling.primaryColor }}
            >
              <Store className="w-4 h-4" /> Browse Marketplace
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Package} label="Installed Tools" value={installedTools.length} color="indigo" />
          <StatCard icon={Code} label="Custom Tools" value={customTools.length} color="gray" />
          <StatCard icon={Star} label="Featured" value={installedTools.filter(t => t.featured).length} color="amber" />
          <StatCard 
            icon={Store} 
            label="Marketplace" 
            value={Object.keys(TOOL_CATALOG).length} 
            color="green"
            onClick={() => setViewState(ViewState.MARKETPLACE)}
          />
        </div>

        {/* Search */}
        {(installedTools.length > 0 || customTools.length > 0) && (
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search installed tools..."
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl"
            />
          </div>
        )}

        {/* Installed Tools */}
        {filteredInstalled.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Installed Tools</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4">
              {filteredInstalled.map(tool => (
                <InstalledToolCard
                  key={tool.id}
                  tool={tool}
                  customStyling={customStyling}
                  onLaunch={handleLaunchTool}
                  onUninstall={handleUninstallTool}
                  onSettings={(tool) => console.log('Settings for', tool.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Custom Tools */}
        {filteredCustom.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Custom Tools</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredCustom.map(tool => (
                <CustomToolCard
                  key={tool.id}
                  tool={tool}
                  onOpen={handleOpenCustomTool}
                  onEdit={(tool) => { setEditingCustomTool(tool); setViewState(ViewState.CUSTOM_EDITOR); }}
                  onDelete={handleDeleteCustomTool}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {installedTools.length === 0 && customTools.length === 0 && (
          <EmptyState
            icon={Package}
            title="No tools installed yet"
            description="Browse the marketplace to discover and install productivity tools for your workspace."
            action={
              <button
                onClick={() => setViewState(ViewState.MARKETPLACE)}
                className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
              >
                <Store className="w-5 h-5" /> Browse Marketplace
              </button>
            }
          />
        )}

        {/* Marketplace CTA */}
        {installedTools.length > 0 && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Discover more tools</h3>
                <p className="text-gray-600 text-sm">
                  Browse {Object.keys(TOOL_CATALOG).length - installedTools.length} more tools in the marketplace
                </p>
              </div>
              <button
                onClick={() => setViewState(ViewState.MARKETPLACE)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <Store className="w-4 h-4" /> Marketplace
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiaryToolsView;